import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction, WritableDraft } from '@reduxjs/toolkit';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppCore from '@/utilities/AppCore';
import { CommunityAndMembership, CommunityMembership, UNKNOWN_COMMUNITY_MEMBERSHIP } from "@/types/community_membership";
import { Match } from "@/types/match";
import { Community, CommunityInfo, UNKNOWN_COMMUNITY } from "@/types/community";
import { UserInfo, UNKNOWN_USER_INFO } from "@/types/user_info";
import { Profile } from "@/types/profile";

export interface UserState {
  domain: string,
  isMainLogicalHost: boolean,
  loggedIn: boolean,
  user_info: UserInfo,
  communityMembership: CommunityMembership,
  communityMemberships: CommunityMembership[],
  current_community: Community,
  current_community_info: CommunityInfo,
  accumulated_public_rabbls: Community[],
  public_rabbls_next_search_offset: number,
  public_rabbls_search: string,
  highlighted_communities1: Community[],
  highlighted_communities2: Community[],
  highlighted_communities2_kevg: CommunityAndMembership[],
  creating_community: Community,
  accumulatedSearchResults: CommunityMembership[],
  next_search_offset: number,
  search_string: string,
  matches: Match[],
  owned_communities: Community[],
  profiles: Profile[],
  drawerOpen: boolean,
  showTest: boolean,
  // When adding new states here, remember to reset them in logoutState if needed.
};

const initialState: UserState = {
  domain: AppCore.getInitialDoman(),
  isMainLogicalHost: AppCore.getInitialIsMainLogicalHost(),
  loggedIn: AppCore.hasAccessToken(),
  user_info: UNKNOWN_USER_INFO,
  communityMembership: UNKNOWN_COMMUNITY_MEMBERSHIP,
  communityMemberships: [],
  current_community: UNKNOWN_COMMUNITY,
  current_community_info: { member_counts: [] },
  accumulated_public_rabbls: [],
  public_rabbls_next_search_offset: 0,
  public_rabbls_search: "",
  highlighted_communities1: [],
  highlighted_communities2: [],
  highlighted_communities2_kevg: [],
  creating_community: UNKNOWN_COMMUNITY,
  accumulatedSearchResults: [],
  next_search_offset: 0,
  search_string: "",
  matches: [],
  owned_communities: [],
  profiles: [],
  drawerOpen: false,
  showTest: false,
} satisfies UserState as UserState;

if (AppUtilities.DEBUGLEVEL.extra) {
  AppUtilities.debug("user initialState loggedIn: " + initialState.loggedIn);
}

function logoutState(state: WritableDraft<UserState>) {
  state.user_info = initialState.user_info;
  state.communityMembership = initialState.communityMembership;
  state.communityMemberships = initialState.communityMemberships;
  state.creating_community = initialState.creating_community;
  state.matches = initialState.matches;
  state.owned_communities = initialState.owned_communities;
  state.profiles = initialState.profiles;
  state.showTest = initialState.showTest;
  state.next_search_offset = 0;
}

export interface InitialData {
  user_info: UserInfo,
  communities: Community[],
  highlighted_communities1: Community[],
  highlighted_communities2: Community[],
  current_community: Community,
  current_community_info: CommunityInfo,
  communityMembership: CommunityMembership,
  communityMemberships: CommunityMembership[],
  creating_community: Community,
  potential_matches: CommunityMembership[],
  matches: Match[],
  owned_communities: Community[],
  profiles: Profile[],
};

export interface CreatingCommunityAndUserInfo {
  user_info: UserInfo;
  current_community: Community;
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setDomain: (state, newState: PayloadAction<string>) => {
      if (AppUtilities.DEBUGLEVEL.dev) {
        AppUtilities.info("user.setDomain");
        AppUtilities.dir(newState.payload);
      }

      state.communityMembership = initialState.communityMembership;
      state.accumulatedSearchResults = initialState.accumulatedSearchResults;
      state.next_search_offset = initialState.next_search_offset;
      state.search_string = initialState.search_string;
      state.matches = initialState.matches;

      state.domain = newState.payload;
      if (state.domain == AppConstants.HOST) {
        state.isMainLogicalHost = true;
      } else {
        state.isMainLogicalHost = false;
      }
    },
    setLoggedIn: (state, newState: PayloadAction<boolean>) => {
      if (AppUtilities.DEBUGLEVEL.dev) {
        AppUtilities.info("user.setLoggedIn");
        AppUtilities.dir(newState.payload);
      }
      state.loggedIn = newState.payload;
      if (!state.loggedIn) {
        logoutState(state);
      }
    },
    removeSearchResult: (state, newState: PayloadAction<CommunityMembership>) => {
      state.accumulatedSearchResults = state.accumulatedSearchResults.filter((s) => s.uniqueid != newState.payload.uniqueid);
    },
    removeSearchResultByID: (state, newState: PayloadAction<string>) => {
      state.accumulatedSearchResults = state.accumulatedSearchResults.filter((s) => s.uniqueid != newState.payload);
    },
    setMatches: (state, newState: PayloadAction<Match[]>) => {
      state.matches = newState.payload;
    },
    appendSearchResults: (state, newState: PayloadAction<any>) => {
      const potential_matches: CommunityMembership[] = ((newState.payload as any).body as any).potential_matches;
      if (potential_matches && potential_matches.length > 0) {
        state.accumulatedSearchResults = state.accumulatedSearchResults.concat(potential_matches);
      }
      const next_search_offset: any = ((newState.payload as any).body as any).next_search_offset;
      if (next_search_offset !== undefined) {
        state.next_search_offset = next_search_offset;
      }
    },
    setData: (state, newState: PayloadAction<any>) => {
      const communityMembership: CommunityMembership = ((newState.payload as any).body as any).community_membership;
      if (communityMembership) {
        state.communityMembership = communityMembership;
      }
      const communityMemberships: CommunityMembership[] = ((newState.payload as any).body as any).community_memberships;
      if (communityMemberships) {
        state.communityMemberships = communityMemberships;
      }
      const communities: Community[] = ((newState.payload as any).body as any).communities;
      if (communities) {
        state.accumulated_public_rabbls = communities;
      }
      const highlighted_communities1: Community[] = ((newState.payload as any).body as any).highlighted_communities1;
      if (highlighted_communities1) {
        state.highlighted_communities1 = highlighted_communities1;
      }
      const highlighted_communities2: Community[] = ((newState.payload as any).body as any).highlighted_communities2;
      if (highlighted_communities2) {
        state.highlighted_communities2 = highlighted_communities2;
      }
      const highlighted_communities2_kevg: CommunityAndMembership[] = ((newState.payload as any).body as any).highlighted_communities2_kevg;
      if (highlighted_communities2_kevg) {
        state.highlighted_communities2_kevg = highlighted_communities2_kevg;
      }
      const current_community: Community = ((newState.payload as any).body as any).community;
      if (current_community) {
        state.current_community = current_community;
      }
      const current_community_info: CommunityInfo = ((newState.payload as any).body as any).community_info;
      if (current_community_info) {
        state.current_community_info = current_community_info;
      }
      const creating_community: Community = ((newState.payload as any).body as any).creating_community;
      if (creating_community) {
        state.creating_community = creating_community;
      }
      const owned_communities: Community[] = ((newState.payload as any).body as any).owned_communities;
      if (owned_communities) {
        state.owned_communities = owned_communities;
      }
      const user_info: UserInfo = ((newState.payload as any).body as any).user_info;
      if (user_info) {
        state.user_info = user_info;
      }
      const profiles: Profile[] = ((newState.payload as any).body as any).profiles;
      if (profiles) {
        state.profiles = profiles;
      }
      const matches: Match[] = ((newState.payload as any).body as any).matches;
      if (matches) {
        state.matches = matches;
      }
      const potential_matches: CommunityMembership[] = ((newState.payload as any).body as any).potential_matches;
      if (potential_matches) {
        state.accumulatedSearchResults = potential_matches;
      }
      const next_search_offset: any = ((newState.payload as any).body as any).next_search_offset;
      if (next_search_offset !== undefined) {
        state.next_search_offset = next_search_offset;
      }
      const public_rabbls_next_search_offset: any = ((newState.payload as any).body as any).public_rabbls_next_search_offset;
      if (public_rabbls_next_search_offset !== undefined) {
        state.public_rabbls_next_search_offset = public_rabbls_next_search_offset;
      }
      const loggedIn: any = ((newState.payload as any).body as any).loggedIn;
      if (loggedIn !== undefined && loggedIn !== null) {
        state.loggedIn = loggedIn;

        const refresh_token: any = ((newState.payload as any).body as any).refresh_token;
        if (refresh_token !== undefined && refresh_token !== null) {
          AppUtilities.login((newState.payload as any).body);
        }
      }
    },
    setDrawerOpen: (state, newState: PayloadAction<boolean>) => {
      state.drawerOpen = newState.payload;
    },
    setShowTest: (state, newState: PayloadAction<boolean>) => {
      state.showTest = newState.payload;
    },
    setSearchString: (state, newState: PayloadAction<string>) => {
      state.search_string = newState.payload;
    },
    setPublicRabblsSearchString: (state, newState: PayloadAction<string>) => {
      state.public_rabbls_search = newState.payload;
    },
    appendPublicRabblsSearchResults: (state, newState: PayloadAction<any>) => {
      const communities: Community[] = ((newState.payload as any).body as any).communities;
      if (communities && communities.length > 0) {
        state.accumulated_public_rabbls = state.accumulated_public_rabbls.concat(communities);
      }
      const public_rabbls_next_search_offset: any = ((newState.payload as any).body as any).public_rabbls_next_search_offset;
      if (public_rabbls_next_search_offset !== undefined) {
        state.public_rabbls_next_search_offset = public_rabbls_next_search_offset;
      }
    },
  },
});

export const {
  setDomain,
  setLoggedIn,
  removeSearchResult,
  removeSearchResultByID,
  setMatches,
  setData,
  setDrawerOpen,
  setShowTest,
  appendSearchResults,
  setSearchString,
  setPublicRabblsSearchString,
  appendPublicRabblsSearchResults,
} = userSlice.actions;

export default userSlice.reducer;
